import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Checkbox,
  FormControlLabel,
  TextField,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  Divider,
  CircularProgress,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import LoginIcon from '@mui/icons-material/Login';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import DownloadIcon from '@mui/icons-material/Download';
import GavelIcon from '@mui/icons-material/Gavel';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { api } from '../services/api';
import { GatedDocument } from '../services/content';

interface ReleaseAgreement {
  signedName: string;
  userId: string;
  signedAt: string;
}

interface DocumentReleaseGateProps {
  documents: GatedDocument[];
  sectionId: string;
  onLoginClick?: () => void;
}

function storageKey(userId: string, sectionId: string): string {
  return `release_agreement_${userId}_${sectionId}`;
}

function getSavedAgreement(userId: string, sectionId: string): ReleaseAgreement | null {
  try {
    const raw = localStorage.getItem(storageKey(userId, sectionId));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

const RELEASE_TERMS = [
  'These documents are provided for informational purposes only and may not be used for construction or modification without explicit written authorization.',
  'I agree not to redistribute, copy, or share these documents with any third party without prior written authorization from the document owner.',
  'I understand that I am solely responsible for verifying the accuracy and applicability of these documents for any intended use.',
  'I acknowledge that these documents may contain proprietary information and that unauthorized use may result in legal action.',
];

const fileIcon = (fileType: string) =>
  fileType === 'pdf' ? <PictureAsPdfIcon color="error" /> : <InsertDriveFileIcon color="primary" />;

const DocumentReleaseGate: React.FC<DocumentReleaseGateProps> = ({
  documents,
  sectionId,
  onLoginClick,
}) => {
  const { isAuthenticated, currentUser } = useAuth();
  const { t } = useLanguage();
  const [agreed, setAgreed] = useState(false);
  const [signedName, setSignedName] = useState('');
  const [hasSigned, setHasSigned] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isChecking, setIsChecking] = useState(false);

  useEffect(() => {
    if (isAuthenticated && currentUser) {
      const existing = getSavedAgreement(currentUser.uid, sectionId);
      if (existing) {
        setHasSigned(true);
        return;
      }

      setIsChecking(true);
      api.checkDocumentRelease(sectionId)
        .then((result) => {
          if (result) {
            const agreement: ReleaseAgreement = {
              signedName: result.signed_name,
              userId: currentUser.uid,
              signedAt: result.signed_at,
            };
            localStorage.setItem(
              storageKey(currentUser.uid, sectionId),
              JSON.stringify(agreement),
            );
            setHasSigned(true);
          }
        })
        .catch(() => {})
        .finally(() => setIsChecking(false));
    }
  }, [isAuthenticated, currentUser, sectionId]);

  if (!isAuthenticated || !currentUser) {
    return (
      <Paper
        elevation={2}
        sx={{
          p: 4,
          textAlign: 'center',
          backgroundColor: 'grey.50',
          borderRadius: 2,
        }}
      >
        <LockIcon sx={{ fontSize: 48, color: 'grey.500', mb: 2 }} />
        <Typography variant="h6" gutterBottom>
          {t.documentGate.signInTitle}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t.documentGate.signInSubtitle}
        </Typography>
        <Button
          variant="contained"
          startIcon={<LoginIcon />}
          onClick={onLoginClick}
        >
          {t.documentGate.signIn}
        </Button>
      </Paper>
    );
  }

  if (hasSigned) {
    return (
      <Box>
        <Alert severity="success" sx={{ mb: 2 }}>
          {t.documentGate.signedNotice}
        </Alert>
        <List>
          {documents.map((doc) => (
            <ListItem
              key={doc.path}
              component="a"
              href={doc.path}
              target="_blank"
              rel="noopener noreferrer"
              download
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                mb: 1,
                textDecoration: 'none',
                color: 'inherit',
                '&:hover': { backgroundColor: 'action.hover' },
              }}
            >
              <ListItemIcon>{fileIcon(doc.fileType)}</ListItemIcon>
              <ListItemText
                primary={doc.name}
                secondary={doc.description}
              />
              <DownloadIcon color="action" />
            </ListItem>
          ))}
        </List>
      </Box>
    );
  }

  const canSubmit = agreed && signedName.trim().length > 0 && !isSubmitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setIsSubmitting(true);

    const agreement: ReleaseAgreement = {
      signedName: signedName.trim(),
      userId: currentUser.uid,
      signedAt: new Date().toISOString(),
    };

    try {
      await api.signDocumentRelease(signedName.trim(), sectionId);
    } catch (err) {
      console.error('Failed to persist release to server:', err);
    }

    localStorage.setItem(
      storageKey(currentUser.uid, sectionId),
      JSON.stringify(agreement),
    );
    setHasSigned(true);
    setIsSubmitting(false);
  };

  return (
    <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
        <GavelIcon color="primary" />
        <Typography variant="h6" component="h3">
          Electronic Document Release Agreement
        </Typography>
      </Box>

      <Divider sx={{ mb: 3 }} />

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Please read and accept the following terms to access the project
        documents.
      </Typography>

      <Box
        sx={{
          backgroundColor: 'grey.50',
          p: 2,
          borderRadius: 1,
          mb: 3,
          border: 1,
          borderColor: 'divider',
        }}
      >
        {RELEASE_TERMS.map((term, idx) => (
          <Typography
            key={idx}
            variant="body2"
            sx={{ mb: idx < RELEASE_TERMS.length - 1 ? 1.5 : 0 }}
          >
            {idx + 1}. {term}
          </Typography>
        ))}
      </Box>

      <FormControlLabel
        control={
          <Checkbox
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            inputProps={{ 'aria-label': 'I have read and agree to the terms above' }}
          />
        }
        label="I have read and agree to the terms above"
      />

      <TextField
        label="Full Name (digital signature)"
        value={signedName}
        onChange={(e) => setSignedName(e.target.value)}
        fullWidth
        size="small"
        sx={{ mt: 2, mb: 3 }}
        placeholder="Type your full name to sign"
      />

      <Button
        variant="contained"
        onClick={handleSubmit}
        disabled={!canSubmit}
        fullWidth
        startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : undefined}
      >
        {isSubmitting ? 'Submitting...' : 'Accept & Continue'}
      </Button>
    </Paper>
  );
};

export default DocumentReleaseGate;
